import React, { FunctionComponent, ReactNode } from "react";
import {
  Pagination,
  PaginationItem,
  Table,
  TableContainer,
  Paper,
  Button,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import styles from "./PaginatedTable.module.css";

interface PaginatedTableProps {
  children: ReactNode;
  page: number;
  count: number; // total pages, passed as 'count' to Pagination
  onChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}

const PaginatedTable: FunctionComponent<PaginatedTableProps> = ({ 
  children, 
  page, 
  count, 
  onChange,
  rowsPerPage,
  rowsPerPageOptions = [10, 20, 50, 100],
  onRowsPerPageChange,
}) => {
  const handleRowsChange = (event: SelectChangeEvent<number>) => {
    const nextRows = Number(event.target.value);
    onRowsPerPageChange?.(nextRows);
  };

  return (
    <>
      <TableContainer component={Paper} className={styles.tableCard} elevation={0}>
        <Table sx={{ minWidth: 650, width: '100%' }} aria-label="paginated table">
            {children}
        </Table>
      </TableContainer>

      <div className={styles.paginationContainer}>
          {typeof rowsPerPage === "number" && onRowsPerPageChange && (
            <div className={styles.rowsPerPageWrapper}>
              <Typography className={styles.showLabel}>Show</Typography>
              <FormControl size="small">
                <Select<number>
                  value={rowsPerPage}
                  onChange={handleRowsChange}
                  className={styles.rowsPerPageSelect}
                >
                  {rowsPerPageOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}
          <Pagination 
              count={count} 
              page={page} 
              onChange={onChange}
              renderItem={(item) => (
                  <PaginationItem
                      slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                      {...item}
                      components={{
                            previous: (props: any) => <Button {...props} startIcon={<ArrowBackIcon />}>Prev</Button>,
                            next: (props: any) => <Button {...props} endIcon={<ArrowForwardIcon />}>Next</Button>
                      }}
                      className={styles.paginationItem}
                  />
              )}
              className={styles.pagination}
          />
      </div>
    </>
  );
};

export default PaginatedTable;
